/**
 * The honeycomb of the custom color picker (KPR-314): which hexagons exist,
 * where they sit, what color each one is, and where an arrow key goes.
 *
 * Pure geometry, no React. Hexagons are pointy-top, in axial coordinates
 * (q across, r down the rows). Units are one hexagon's circumradius, so a cell
 * is sqrt(3) wide and a row is 1.5 down; the component scales the whole box.
 *
 * The colors read like the honeycomb a person has met in other pickers: white
 * in the middle, the hue going around, saturation building to a pure ring and
 * brightness then falling to the rim. A hue wheel has no grays, so the grays
 * are their own short strip.
 */

import { hsbToHex } from './colorModels';

const SQRT3 = Math.sqrt(3);

/** The six neighbours in axial steps. Names are screen directions. */
const STEPS = Object.freeze({
	E: [1, 0], W: [-1, 0], NE: [1, -1], NW: [0, -1], SE: [0, 1], SW: [-1, 1],
});

/** How dark the outermost ring gets, as a brightness percentage. */
const RIM_BRIGHTNESS = 30;

const cellOf = (q, r, color) => ({
	q, r,
	// The cell's centre, in circumradius units.
	x: SQRT3 * (q + (r / 2)),
	y: 1.5 * r,
	...color,
	hex: hsbToHex(color.hsb),
});

/** The box (min-x, min-y, width, height) that holds cells of those extents. */
const boxFor = (cells) => {
	const xs = cells.map(c => c.x);
	const ys = cells.map(c => c.y);
	const minX = Math.min(...xs) - (SQRT3 / 2);
	const minY = Math.min(...ys) - 1;
	return { x: minX, y: minY, width: Math.max(...xs) + (SQRT3 / 2) - minX, height: Math.max(...ys) + 1 - minY };
};

/**
 * A honeycomb `rings` rings deep: 1 + 3·rings·(rings + 1) cells (127 at six,
 * 37 at three). In reading order, so the DOM order is the visual order.
 */
export function honeycombCells(rings) {
	const pure = Math.round(rings / 2);
	const cells = [];
	for (let r = -rings; r <= rings; r += 1) {
		for (let q = Math.max(-rings, -r - rings); q <= Math.min(rings, -r + rings); q += 1) {
			const ring = Math.max(Math.abs(q), Math.abs(r), Math.abs(-q - r));
			const x = SQRT3 * (q + (r / 2));
			const y = 1.5 * r;
			let hsb;
			if (ring === 0) {
				hsb = { h: 0, s: 0, b: 100 };
			} else {
				// Whole degrees: a hue is read off the cell's own angle, so the
				// colors fan out like spokes rather than stepping per ring.
				const h = Math.round(((Math.atan2(-y, x) * 180) / Math.PI + 360) % 360) % 360;
				hsb = ring <= pure
					? { h, s: Math.round((100 * ring) / pure), b: 100 }
					: { h, s: 100, b: Math.round(100 - ((100 - RIM_BRIGHTNESS) * (ring - pure)) / (rings - pure)) };
			}
			cells.push(cellOf(q, r, { ring, hsb }));
		}
	}
	return { cells, box: boxFor(cells), rings };
}

/** White to black in seven steps, as a one-row strip. */
export function grayStripCells(count = 7) {
	const cells = Array.from({ length: count }, (_, q) => (
		cellOf(q, 0, { ring: q, hsb: { h: 0, s: 0, b: Math.round(100 - ((100 * q) / (count - 1))) } })
	));
	return { cells, box: boxFor(cells), rings: 0 };
}

/**
 * The cell an arrow key leads to, as { index, goalX }.
 *
 * Left and right step along the row. Up and down cannot go straight on a
 * honeycomb: each row is offset half a cell, so the choice is between the two
 * cells above (or below). It takes the one nearest the column the walk began
 * in (`goalX`), so up-up-down-down returns to where it started and a vertical
 * walk stays in line. On a tie it prefers the side nearer the middle, then
 * the left. A move off the edge stays put. Home goes to the middle.
 *
 * `goalX` is the remembered column: null at the start of a walk, kept across
 * vertical moves, reset by anything else.
 */
export function neighborIndex(cells, index, key, goalX = null) {
	const here = cells[index];
	if (!here) return { index, goalX };
	const at = (q, r) => cells.findIndex(c => c.q === q && c.r === r);
	const go = (name) => at(here.q + STEPS[name][0], here.r + STEPS[name][1]);

	if (key === 'Home') {
		const middle = cells.findIndex(c => c.ring === 0);
		return middle >= 0 ? { index: middle, goalX: null } : { index, goalX };
	}
	if (key === 'ArrowLeft' || key === 'ArrowRight') {
		const next = go(key === 'ArrowLeft' ? 'W' : 'E');
		return next >= 0 ? { index: next, goalX: null } : { index, goalX: null };
	}
	if (key === 'ArrowUp' || key === 'ArrowDown') {
		const candidates = (key === 'ArrowUp' ? ['NW', 'NE'] : ['SW', 'SE'])
			.map(go)
			.filter(i => i >= 0);
		const want = goalX === null ? here.x : goalX;
		if (!candidates.length) return { index, goalX: want };
		const best = candidates.reduce((a, b) => {
			const da = Math.abs(cells[a].x - want);
			const db = Math.abs(cells[b].x - want);
			if (Math.abs(da - db) > 1e-9) return da < db ? a : b;
			const ma = Math.abs(cells[a].x);
			const mb = Math.abs(cells[b].x);
			if (Math.abs(ma - mb) > 1e-9) return ma < mb ? a : b;
			return cells[a].x <= cells[b].x ? a : b;
		});
		return { index: best, goalX: want };
	}
	return { index, goalX };
}
